/**
 * `useSelectionMode` — hook owning the state for transformable data tables.
 *
 * Encapsulates the "table can flip into a bulk-actions mode" pattern:
 * - `mode` boolean ("am I in selection mode?")
 * - `rowSelection` dict (MRT native shape) + derived `selectedIds`
 * - Enter / exit lifecycle with invariant: selection cleared on exit
 *
 * Call it in the parent component and pass its returned state to SDataTable
 * via `selectionMode` + `state.rowSelection` + `onRowSelectionChange`.
 *
 * Layered API — Layer 1 of the three-layer selection-mode design. Callers
 * who want a turnkey UI pick up `SelectionModeToggle` and
 * `SelectionActionsBar` primitives (Layer 3); callers wiring a custom
 * section header use this hook alone.
 *
 * @example
 * ```tsx
 * const selection = useSelectionMode();          // defaults: mode=false
 * const alwaysOn  = useSelectionMode({ defaultMode: true }); // TasksTable
 *
 * <SDataTable
 *   columns={...}
 *   data={...}
 *   selectionMode={selection.mode}
 *   state={{ rowSelection: selection.rowSelection }}
 *   onRowSelectionChange={selection.setRowSelection}
 * />
 * ```
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { SDataTableRowSelectionState } from './SDataTable.types';

export interface UseSelectionModeOptions {
  /**
   * Initial mode. Default `false` (normal / navigate mode). Pass `true` to
   * render with selection always on (e.g. TasksTable — bulk UX is primary).
   */
  defaultMode?: boolean;

  /**
   * Fires whenever the derived `selectedIds` array changes. Useful for
   * legacy callers whose public contract is `onSelectionChange(string[])`
   * (e.g. TasksTable's parent `ProjectViewPage.jsx`).
   */
  onSelectionChange?: (ids: string[]) => void;
}

export interface SelectionModeState {
  /** Current mode. `true` = checkboxes visible + row click toggles selection. */
  mode: boolean;
  /**
   * Imperative setter. Setting to `false` also clears selection (enforces
   * the invariant "selection makes no sense when not in mode").
   */
  setMode: (next: boolean) => void;
  /** Sugar: `setMode(true)`. */
  enter: () => void;
  /** Sugar: `setMode(false)` + clearSelection. Use for the "Done" button. */
  exit: () => void;
  /** MRT-native dict shape. Pass directly to `SDataTable.state.rowSelection`. */
  rowSelection: SDataTableRowSelectionState;
  /** Pass to `SDataTable.onRowSelectionChange`. */
  setRowSelection: React.Dispatch<
    React.SetStateAction<SDataTableRowSelectionState>
  >;
  /**
   * Derived: array of selected row IDs (keys where the dict value is truthy).
   * Memoized; safe to use in effect/render without re-render spam.
   */
  selectedIds: string[];
  /** Setter: `setRowSelection({})`. Does NOT change mode. */
  clearSelection: () => void;
  /** Derived: `selectedIds.length > 0`. */
  hasSelection: boolean;
}

export function useSelectionMode(
  options: UseSelectionModeOptions = {},
): SelectionModeState {
  const { defaultMode = false, onSelectionChange } = options;

  const [mode, setModeInternal] = useState<boolean>(defaultMode);
  const [rowSelection, setRowSelection] =
    useState<SDataTableRowSelectionState>({});

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((k) => rowSelection[k]),
    [rowSelection],
  );

  // Emit onSelectionChange only when `selectedIds` actually changes (not on
  // unrelated re-renders). We compare shallow array equality via a ref.
  const prevIdsRef = useRef<string[] | null>(null);
  useEffect(() => {
    const prev = prevIdsRef.current;
    const changed =
      prev === null ||
      prev.length !== selectedIds.length ||
      prev.some((id, i) => id !== selectedIds[i]);
    if (changed) {
      prevIdsRef.current = selectedIds;
      onSelectionChange?.(selectedIds);
    }
    // onSelectionChange is intentionally excluded — callers often pass
    // a fresh inline function each render; we track changes via selectedIds
    // only. Callers who need onSelectionChange-identity-stability should
    // memoize it at the call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds]);

  const clearSelection = useCallback(() => {
    setRowSelection({});
  }, []);

  const setMode = useCallback((next: boolean) => {
    setModeInternal(next);
    if (!next) {
      // Invariant: mode=false implies selection={}. Enforce on exit.
      setRowSelection({});
    }
  }, []);

  const enter = useCallback(() => setMode(true), [setMode]);
  const exit = useCallback(() => setMode(false), [setMode]);

  return {
    mode,
    setMode,
    enter,
    exit,
    rowSelection,
    setRowSelection,
    selectedIds,
    clearSelection,
    hasSelection: selectedIds.length > 0,
  };
}
